import { useState, useEffect } from 'react';
import { supabasePortal as supabase } from '../lib/supabase';
import { detectLang } from './portalI18n';
import { i18n } from './portalI18n';
import PortalLoginPage from './PortalLoginPage';
import PortalRegisterPage from './PortalRegisterPage';
import PortalSelfRegisterPage from './PortalSelfRegisterPage';
import PortalDashboard from './PortalDashboard';
import PortalResetPasswordPage from './PortalResetPasswordPage';

/**
 * PortalApp — top-level router for the /portal path.
 *
 * URL patterns:
 *   /portal                → PortalLoginPage
 *   /portal?token=xxx      → PortalRegisterPage (first-time setup)
 *
 * State machine:
 *   idle → checking session → (loggedIn | needsLogin | needsRegister)
 */
export default function PortalApp() {
  const params = new URLSearchParams(window.location.search);
  const registerToken = params.get('token');

  const [lang, setLang] = useState(detectLang);
  const t = i18n[lang];

  const isSelfReg = params.get('mode') === 'self-register';

  // 'checking' | 'login' | 'register' | 'self-register' | 'loading-data' | 'ready' | 'error' | 'reset-password'
  const [phase, setPhase] = useState(isSelfReg ? 'self-register' : registerToken ? 'register' : 'checking');
  const [session, setSession] = useState(null);
  const [clientData, setClientData] = useState(null);   // { funds, fileInfo, clientName }
  const [dataError, setDataError] = useState('');

  // On mount: check if there's already a valid portal session
  useEffect(() => {
    if (registerToken || isSelfReg) return; // skip — we're in registration flow
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setSession(data.session);
        setPhase('loading-data');
      } else {
        setPhase('login');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSession(sess);
        setPhase('reset-password');
      } else if (!sess) {
        setPhase('login'); setSession(null); setClientData(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  // Fetch client data whenever we have a session
  useEffect(() => {
    if (phase !== 'loading-data' || !session) return;
    const token = session.access_token;
    fetch('/api/portal/client-data', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setClientData(data);
        setPhase('ready');
      })
      .catch(err => {
        console.error('[PortalApp] client-data error:', err);
        setDataError(t.loadError);
        setPhase('error');
      });
  }, [phase, session, t.loadError]);

  const handleLoginSuccess = (sess) => {
    setSession(sess);
    setPhase('loading-data');
  };

  const handleRegisterSuccess = (sess) => {
    // Clear token from URL without reload
    window.history.replaceState(null, '', '/portal');
    setSession(sess);
    setPhase('loading-data');
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setClientData(null);
    setPhase('login');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === 'checking') {
    return <FullPageSpinner message={t.loading} />;
  }

  if (phase === 'register') {
    return <PortalRegisterPage token={registerToken} onSuccess={handleRegisterSuccess} />;
  }

  if (phase === 'self-register') {
    return <PortalSelfRegisterPage onSuccess={handleRegisterSuccess} />;
  }

  if (phase === 'login') {
    return <PortalLoginPage onSuccess={handleLoginSuccess} />;
  }

  if (phase === 'reset-password') {
    return <PortalResetPasswordPage onSuccess={handleLoginSuccess} />;
  }

  if (phase === 'loading-data') {
    return <FullPageSpinner message={t.loading} />;
  }

  if (phase === 'error') {
    return (
      <div style={centerStyle}>
        <div style={{ textAlign: 'center', background: 'white', borderRadius: 16, padding: 40, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <p style={{ color: '#dc2626', fontWeight: 600 }}>{dataError}</p>
          <button
            onClick={() => { setPhase('loading-data'); setDataError(''); }}
            style={{ marginTop: 16, padding: '10px 24px', borderRadius: 10, border: 'none', background: '#4338ca', color: 'white', cursor: 'pointer', fontWeight: 600 }}
          >
            🔄 נסה שוב
          </button>
          <br />
          <button onClick={handleSignOut} style={{ marginTop: 12, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>
            {t.logout}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'ready' && clientData) {
    return (
      <PortalDashboard
        familyMembers={clientData.familyMembers || []}
        advisorLogo={clientData.advisorLogo}
        lang={lang}
        onSignOut={handleSignOut}
      />
    );
  }

  return null;
}

function FullPageSpinner({ message }) {
  return (
    <div style={{ ...centerStyle, background: 'linear-gradient(150deg, #f1f5f9, #e9eef8)', direction: 'rtl' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, fontFamily: "'Segoe UI', sans-serif" }}>
        <div style={{ fontSize: 48 }}>🏦</div>
        <p style={{ color: '#64748b', fontSize: 14 }}>{message}</p>
        <div style={{ width: 180, height: 3, background: '#dde4f0', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{
            width: '38%', height: '100%', borderRadius: 99,
            background: 'linear-gradient(90deg, #818cf8, #4338ca)',
            animation: 'portalBar 1.7s cubic-bezier(.4,0,.2,1) infinite',
          }} />
        </div>
        <style>{`@keyframes portalBar { 0% { transform:translateX(-100%) } 100% { transform:translateX(380%) } }`}</style>
      </div>
    </div>
  );
}

const centerStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
